import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Avatar, Tooltip } from '@mui/material';
import { CiUser } from 'react-icons/ci';
import {
  MdOutlineDashboard, MdOutlineImage, MdOutlineArticle,
  MdOutlineSettings, MdOutlineStarOutline, MdOutlineRedeem,
  MdOutlineCompareArrows,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { HiOutlineUsers, HiOutlineChatBubbleLeftRight, HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import { TbReportAnalytics } from 'react-icons/tb';
import { RiBuilding2Line } from 'react-icons/ri';
import logo from 'assets/img/logo.png';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    group: 'Usuarios',
    items: [
      { label: 'Usuarios',              path: '/usuarios',           icon: <HiOutlineUsers size={17} /> },
      { label: 'Gestión Enfermeras',    path: '/reporte-enfermeras', icon: <TbReportAnalytics size={17} /> },
      { label: 'Contactos WhatsApp',    path: '/contactos-whatsapp', icon: <FaWhatsapp size={16} /> },
      { label: 'Solicitudes de cotización', path: '/reporte-cotizaciones', icon: <HiOutlineClipboardDocumentList size={17} /> },
      { label: 'Calificaciones',        path: '/calificaciones',     icon: <MdOutlineStarOutline size={17} /> },
    ],
  },
  {
    group: 'Contenido',
    items: [
      { label: 'Banners',    path: '/banners',   icon: <MdOutlineImage size={17} /> },
      { label: 'Blog',       path: '/admin-blog', icon: <MdOutlineArticle size={17} /> },
      { label: 'Empresas',   path: '/empresas',  icon: <RiBuilding2Line size={17} /> },
      { label: 'Distintivos', path: '/distintivos', icon: <MdOutlineRedeem size={17} /> },
    ],
  },
  {
    group: 'Comunicación',
    items: [
      { label: 'Mensajes',  path: '/admin-mensajes', icon: <HiOutlineChatBubbleLeftRight size={17} /> },
    ],
  },
  {
    group: 'Sistema',
    items: [
      { label: 'Configuración',  path: '/configuracion',  icon: <MdOutlineSettings size={17} /> },
      { label: 'Redirecciones',  path: '/redirecciones',  icon: <MdOutlineCompareArrows size={17} /> },
    ],
  },
];

interface Props {
  userName: string;
  userImage: string;
}

export default function AdminSidebar({ userName, userImage }: Props) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const logout = () => {
    localStorage.removeItem('ltkn');
    localStorage.removeItem('user_id');
    localStorage.removeItem('role_id');
    window.location.href = '/';
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <aside
      style={{
        width: collapsed ? 64 : 220,
        minWidth: collapsed ? 64 : 220,
        height: '100vh',
        background: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.22s ease, min-width 0.22s ease',
        overflow: 'hidden',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Logo + collapse toggle */}
      <div style={{
        padding: collapsed ? '20px 0' : '20px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        borderBottom: '1px solid #1e293b',
        minHeight: 68,
      }}>
        {!collapsed && (
          <Link to="/">
            <img src={logo} alt="Logo" style={{ height: 32, filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
          </Link>
        )}
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#64748b', padding: 6, borderRadius: 6, lineHeight: 1,
            display: 'flex', alignItems: 'center',
          }}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          <MdOutlineDashboard size={18} />
        </button>
      </div>

      {/* Nav groups */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
        {navGroups.map((group) => (
          <div key={group.group}>
            {!collapsed && (
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                color: '#475569', padding: '12px 16px 4px',
                textTransform: 'uppercase',
              }}>
                {group.group}
              </div>
            )}
            {collapsed && <div style={{ margin: '8px 0 4px', borderTop: '1px solid #1e293b' }} />}
            {group.items.map((item) => {
              const active = isActive(item.path);
              const el = (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: collapsed ? '10px 0' : '9px 16px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    margin: '1px 8px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    fontSize: 13,
                    fontWeight: active ? 600 : 400,
                    color: active ? '#fff' : '#94a3b8',
                    background: active ? '#1d4ed8' : 'transparent',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = '#1e293b'; (e.currentTarget as HTMLElement).style.color = '#e2e8f0'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#94a3b8'; }}
                >
                  <span style={{ flexShrink: 0, color: active ? '#93c5fd' : 'inherit' }}>{item.icon}</span>
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );
              return collapsed
                ? <Tooltip key={item.path} title={item.label} placement="right" arrow>{el}</Tooltip>
                : el;
            })}
          </div>
        ))}
      </nav>

      {/* User + logout */}
      <div style={{ borderTop: '1px solid #1e293b', padding: collapsed ? '12px 0' : '12px 12px' }}>
        {collapsed ? (
          <Tooltip title={`${userName} — Cerrar sesión`} placement="right" arrow>
            <div
              onClick={logout}
              style={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
            >
              <Avatar src={userImage} sx={{ width: 32, height: 32, bgcolor: '#1d4ed8', fontSize: 14 }}>
                <CiUser size={18} />
              </Avatar>
            </div>
          </Tooltip>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar src={userImage} sx={{ width: 32, height: 32, bgcolor: '#1d4ed8', fontSize: 14 }}>
              <CiUser size={18} />
            </Avatar>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userName || 'Admin'}
              </div>
              <button
                onClick={logout}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#64748b', padding: 0 }}
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
